import React, { Component } from 'react';
import styled from 'styled-components';
import propTypes from 'prop-types';

import CustomizedDropdown from '@components/common/dropdowns/Dropdown';
import breakpointsMinWidth from '@styling/breakpointsMinWidth';
import { FormatTypes } from '@constants/templateDictionaries';
import { EditListingIcon } from '@components/IRIS/common/irisIcons';
import keyCodes from '@components/userDocuments/editDocument/editJournal/constants/keyCodes';
import {
  DownArrow,
  FacebookNew,
  TwitterNew,
  GithubNew,
  LinkedInNew,
  GlobeNew,
  BehanceNew,
  Dribble,
  GoodReads,
  Instagram,
  Vsco,
  Codepen,
  AngelList,
  StackOverflow,
  GoogleSchollar,
  ResearchGate,
  Kraggle,
  Medium,
  Youtube,
} from '../../../../../../editJournal/toolset/assets/icons';
import profileLinkOpts from './profileLinkOpts';
import { organizationLabels } from './profileLinkEnums';

const IconSelector = styled.div`
  height: 100%;
  border-radius: 2px;
  pointer-events: auto;
  cursor: pointer;
  position: relative;
  user-select: none;
  -webkit-user-select: none;

  .personalLinkOpts {
    margin-top: 4px;
    z-index: 2;
  }
`;

const LinkIcon = styled.span`
  padding-right: 2px;

  @media ${breakpointsMinWidth.desktopMd} {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const DropDownIcon = styled.span``;

const ActiveElement = styled.div`
  display: flex;
  align-items: center;

  padding: 0 7px;
  height: 36px;
  background-color: #f3f3f3;
  border-radius: 4px;
  transform-origin: center;
`;

const StyledInput = styled.input`
  border: none;
  background-color: transparent;
  outline: none;
  flex: 1;
  font-size: 12px;
  max-width: ${({ isEditing }) => (isEditing ? '85px' : '73px')};
  padding: 0 2px;
  margin-right: 4px;
  border-radius: 4px;
  ${({ isEditing }) => !isEditing && 'pointer-events: none;'}
  ${({ isEditing }) => isEditing && 'background: white;'}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${breakpointsMinWidth.tabletSm} {
    max-width: ${({ isEditing }) => (isEditing ? '69px' : '57px')};
  }
  @media ${breakpointsMinWidth.tabletLg} {
    max-width: ${({ isEditing }) => (isEditing ? '61px' : '49px')};
  }
  @media ${breakpointsMinWidth.desktopSm} {
    max-width: ${({ isEditing }) => (isEditing ? '85px' : '73px')};
  }
  @media ${breakpointsMinWidth.desktopMd} {
    max-width: ${({ isEditing }) => (isEditing ? '113px' : '101px')};
    font-size: 14px;
  }
`;

class PersonalLinkIconSelector extends Component {
  state = {
    isEditing: false,
    value: '',
    show: false,
  };

  iconSelector = React.createRef();

  inputRef = React.createRef();

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    // Set initial value from getInitialText
    const initialValue = this.getInitialText();
    this.setState({ value: initialValue });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e) => {
    const { isEditing } = this.state;
    if (!this.iconSelector?.current?.contains(e.target)) {
      this.setState({ show: false });
      if (isEditing) {
        this.handleSave();
      }
    }
  };

  handleOpenDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // if clicked on the input element, do not open the dropdown.
    if (
      this.inputRef?.current?.contains(e.target) ||
      e.target?.classList?.contains('input-wrapper')
    ) {
      const { isEditing } = this.state;
      if (isEditing) return;
      const { editor, node } = this.props;
      editor?.moveToRangeOfNode(node)?.moveToEndOfNode(node)?.focus();
    }

    const { show, value } = this.state;
    this.setState({ show: !show });
    const savedValue = this.getInitialText();

    if (value !== savedValue) {
      this.handleSave();
    }
  };

  handleCloseDropdown = (e) => {
    e.preventDefault();
    this.setState({ show: false });
  };

  updateButton = (selectedIcon) => {
    const { editor, node } = this.props;
    const dataJSON = node?.data?.toJSON();
    editor.setNodeByKey(node.key, {
      data: {
        ...dataJSON,
        organization: selectedIcon,
      },
    });
    const updatedText = this.getInitialText(selectedIcon);
    this.setState({ value: updatedText });
  };

  handleEditIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isEditing: true }, () => {
      const { editor } = this.props;
      editor?.blur();
      this.inputRef?.current?.focus();
    });
  };

  handleInputChange = (e) => {
    this.setState({ value: e.target.value });
  };

  handleSave = () => {
    const { value } = this.state;
    const { editor, node } = this.props;
    const organization = node?.data?.get('organization') || 'others';
    if (!value.trim()) {
      this.setState({
        isEditing: false,
        value: this.getInitialText(organization),
      });
      return;
    }
    const dataJSON = node?.data?.toJSON();
    const organizationTextString = dataJSON?.organizationText;
    const organizationText = organizationTextString
      ? JSON.parse(organizationTextString)
      : {};
    organizationText[organization] = value;
    editor?.setNodeByKey(node?.key, {
      data: {
        ...dataJSON,
        organizationText: JSON.stringify(organizationText),
      },
    });
    this.setState({ isEditing: false });
  };

  getInitialText = (selectedIcon) => {
    const { editor, node } = this.props;
    if (!editor) return '';
    let organization = selectedIcon;
    const { data: nodeData } = node;
    const nodeDataJSON = nodeData?.toJSON() || {};
    const organizationTextString = nodeDataJSON?.organizationText;
    if (!organization) {
      organization = nodeDataJSON?.organization || 'others';
    }
    const organizationText = organizationTextString
      ? JSON.parse(organizationTextString)
      : {};

    return (
      organizationText[organization] || organizationLabels[organization] || ''
    );
  };

  handleKeyDown = (e) => {
    if (e.keyCode === keyCodes.ENTER) {
      this.handleEditIconClick();
    }
  };

  render() {
    const { show, value, isEditing } = this.state;
    const { node, formatType } = this.props;
    const organizationType = node?.data?.get('organization') || 'others';

    const inputValue = isEditing
      ? value
      : value.trim() || this.getInitialText(organizationType);

    return (
      <IconSelector
        spellCheck={false}
        draggable
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <ActiveElement
          ref={this.iconSelector}
          onClick={this.handleOpenDropdown}
        >
          <LinkIcon>
            {
              {
                others: (
                  <GlobeNew
                    className="personalLinkIcon"
                    key="defaultPersonalLink"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                linkedIn: (
                  <LinkedInNew
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                github: (
                  <GithubNew
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_github`}
                  />
                ),
                behance: (
                  <BehanceNew
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                twitter: (
                  <TwitterNew
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                facebook: (
                  <FacebookNew
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                youTube: (
                  <Youtube
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                dribble: (
                  <Dribble
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                medium: (
                  <Medium
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                kaggle: (
                  <Kraggle
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                researchGate: (
                  <ResearchGate
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                googleScholar: (
                  <GoogleSchollar
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                stackOverflow: (
                  <StackOverflow
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                angelList: (
                  <AngelList
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                codePen: (
                  <Codepen
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                vsco: (
                  <Vsco
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                instagram: (
                  <Instagram
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
                goodreads: (
                  <GoodReads
                    className="personalLinkIcon"
                    width="14px"
                    height="14px"
                    dataTestId={`${node.type}_icon_selector_${organizationType}`}
                  />
                ),
              }[organizationType]
            }
          </LinkIcon>
          <div
            className={`flex px-[4px] ${isEditing ? 'items-end pb-[2px] h-[24px]' : 'items-baseline'} input-wrapper`}
            contentEditable={false}
          >
            <StyledInput
              ref={this.inputRef}
              type="text"
              value={inputValue}
              onChange={this.handleInputChange}
              readOnly={!isEditing}
              isEditing={isEditing}
            />
            <div
              onClick={this.handleEditIconClick}
              onKeyDown={this.handleKeyDown}
              className={`cursor-pointer ${isEditing ? 'hidden' : 'block'}`}
              aria-label="Edit Personal Link Text"
              role="button"
              tabIndex={0}
            >
              <EditListingIcon width={12} height={12} />
            </div>
          </div>
          <DropDownIcon>
            <DownArrow
              color="#473880"
              width="14px"
              height="14px"
              className="personalLinkIcon"
            />
          </DropDownIcon>
        </ActiveElement>
        <div>
          <CustomizedDropdown
            isOpen={show}
            top={formatType === FormatTypes.COVER_LETTER ? '-154' : '38'}
            left="0"
            closeDropdown={() => this.setState({ show: false })}
            itemsList={profileLinkOpts({
              updateButton: this.updateButton,
              node,
            })}
            sectioned={false}
            border={false}
            styleClasses="w-[144px] desktopMd:w-[160px] text-left [&>div]:!py-[4px] max-h-[150px] desktopMd:max-h-[164px] !rounded-[10px]"
            itemStyleClasses="py-[2px] [&>span]:text-[12px] [&>span]:desktopMd:text-[14px] [&>svg]:w-[16px] [&>svg]:h-[16px] desktopMd:py-[4px] [&>span]:inline-flex [&>span]:items-center"
          />
        </div>
      </IconSelector>
    );
  }
}

PersonalLinkIconSelector.propTypes = {
  node: propTypes.objectOf(propTypes.object).isRequired,
  editor: propTypes.shape({
    resolveController: propTypes.func,
  }).isRequired,
};

export default PersonalLinkIconSelector;
